import React, { useState } from 'react';
import CustomerShell from '../../components/customer/CustomerShell';
import CustomerTypeBadge from '../../components/admin/CustomerTypeBadge';
import { useCustomerAuth } from '../../App';
import { customerApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { formatDateTime } from '../../lib/utils';
import { User, Phone, Mail, MessageCircle, Edit2, Save, X, CheckCircle2, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';

export default function AccountProfilePage() {
  const { customer, updateCustomerData } = useCustomerAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: customer?.full_name || '',
    email: customer?.email || '',
    whatsapp_number: customer?.whatsapp_number || '',
    marketing_opt_in: customer?.marketing_opt_in ?? true,
  });

  // Password section state
  const hasPassword = !!customer?.password_set || (!!customer?.password_hash);
  const [pwdEditing, setPwdEditing] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await customerApi.updateProfile(form);
      updateCustomerData(res.data);
      setEditing(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (pwdForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (hasPassword && !pwdForm.current_password) {
      toast.error('Enter your current password to change it');
      return;
    }
    setPwdSaving(true);
    try {
      const payload = {
        new_password: pwdForm.new_password,
        ...(hasPassword ? { current_password: pwdForm.current_password } : {}),
      };
      await customerApi.setPassword(payload);
      // Reflect that password is now set so the UI updates immediately
      updateCustomerData({ ...customer, password_set: true });
      setPwdEditing(false);
      setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success(hasPassword ? 'Password updated' : 'Password set — you can now login with phone + password');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setPwdSaving(false);
    }
  };

  const initials = customer?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <CustomerShell title="My Profile">
      <div className="max-w-md mx-auto space-y-5">
        {/* Avatar + name hero */}
        <div className="surface-card card-highlight rounded-2xl p-5 text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center mx-auto mb-3">
            <span className="text-[#120F08] font-bold text-2xl">{initials}</span>
          </div>
          <h2 className="font-['EB_Garamond'] text-2xl text-[#F2F2F2]">{customer?.full_name}</h2>
          <p className="text-[#B9B9B9] text-sm">{customer?.phone}</p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <CustomerTypeBadge customerType={customer?.customer_type || 'basic'} />
          </div>
          {customer?.created_at && (
            <p className="text-[#6F6F6F] text-xs mt-1">Member since {formatDateTime(customer.created_at)}</p>
          )}
        </div>

        {/* Profile form */}
        <div className="surface-card card-highlight rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Account Details</h3>
            {!editing ? (
              <Button size="sm" variant="ghost" className="gap-1.5 text-[#B9B9B9] text-xs h-7" onClick={() => setEditing(true)}>
                <Edit2 size={12} /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="text-xs h-7 text-[#B9B9B9]" onClick={() => setEditing(false)}>
                  <X size={12} /> Cancel
                </Button>
                <Button size="sm" className="btn-gold gap-1 text-xs h-7" onClick={handleSave} disabled={saving}>
                  <Save size={12} /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><User size={11} /> Full Name</Label>
              {editing ? (
                <Input value={form.full_name} onChange={e => setForm(f => ({...f, full_name: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" />
              ) : (
                <p className="text-[#F2F2F2] text-sm">{customer?.full_name || '—'}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Phone size={11} /> Phone</Label>
              <p className="text-[#F2F2F2] text-sm">{customer?.phone}</p>
              <p className="text-[#6F6F6F] text-xs">Phone number cannot be changed</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><Mail size={11} /> Email</Label>
              {editing ? (
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" placeholder="email@example.com" />
              ) : (
                <p className="text-[#F2F2F2] text-sm">{customer?.email || '—'}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[#B9B9B9] text-xs flex items-center gap-1.5"><MessageCircle size={11} /> WhatsApp</Label>
              {editing ? (
                <Input value={form.whatsapp_number} onChange={e => setForm(f => ({...f, whatsapp_number: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" placeholder="WhatsApp number" />
              ) : (
                <p className="text-[#F2F2F2] text-sm">{customer?.whatsapp_number || '—'}</p>
              )}
            </div>

            {/* Marketing opt-in */}
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-[#B9B9B9] text-xs">Event & Offer Updates</Label>
                <p className="text-[#6F6F6F] text-[10px] mt-0.5">Receive WhatsApp updates about events</p>
              </div>
              <Switch
                checked={editing ? form.marketing_opt_in : (customer?.marketing_opt_in ?? true)}
                onCheckedChange={v => editing && setForm(f => ({...f, marketing_opt_in: v}))}
                disabled={!editing}
              />
            </div>
          </div>
        </div>

        {/* Password Section */}
        <div className="surface-card card-highlight rounded-2xl p-5" data-testid="profile-password-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock size={14} className="text-[#D4A843]" />
              <h3 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">
                {hasPassword ? 'Password' : 'Set a Password'}
              </h3>
            </div>
            {!pwdEditing ? (
              <Button
                size="sm"
                variant="ghost"
                data-testid="profile-edit-password-button"
                className="gap-1.5 text-[#B9B9B9] text-xs h-7"
                onClick={() => setPwdEditing(true)}
              >
                {hasPassword ? <><Edit2 size={12} /> Change</> : <><KeyRound size={12} /> Set Password</>}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="profile-cancel-password-button"
                  className="text-xs h-7 text-[#B9B9B9]"
                  onClick={() => {
                    setPwdEditing(false);
                    setPwdForm({ current_password: '', new_password: '', confirm_password: '' });
                  }}
                >
                  <X size={12} /> Cancel
                </Button>
                <Button
                  size="sm"
                  data-testid="profile-save-password-button"
                  className="btn-gold gap-1 text-xs h-7"
                  onClick={handleSetPassword}
                  disabled={pwdSaving}
                >
                  <Save size={12} /> {pwdSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>

          {!pwdEditing ? (
            <p className="text-[#B9B9B9] text-xs leading-relaxed">
              {hasPassword
                ? 'Your account is secured with a password. You can sign in with phone + password OR via OTP from the homepage.'
                : 'You currently log in via OTP. Set a password so you can also sign in with phone + password.'}
            </p>
          ) : (
            <div className="space-y-3">
              {hasPassword && (
                <div className="space-y-1.5">
                  <Label className="text-[#B9B9B9] text-xs">Current Password</Label>
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    data-testid="profile-current-password-input"
                    value={pwdForm.current_password}
                    onChange={e => setPwdForm(p => ({ ...p, current_password: e.target.value }))}
                    className="bg-[#111111] border-[#2A2A2A]"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-[#B9B9B9] text-xs">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    data-testid="profile-new-password-input"
                    value={pwdForm.new_password}
                    onChange={e => setPwdForm(p => ({ ...p, new_password: e.target.value }))}
                    className="bg-[#111111] border-[#2A2A2A] pr-10"
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6F6F6F] hover:text-[#B9B9B9]"
                    data-testid="profile-toggle-password-visibility"
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#B9B9B9] text-xs">Confirm New Password</Label>
                <Input
                  type={showPwd ? 'text' : 'password'}
                  data-testid="profile-confirm-password-input"
                  value={pwdForm.confirm_password}
                  onChange={e => setPwdForm(p => ({ ...p, confirm_password: e.target.value }))}
                  className="bg-[#111111] border-[#2A2A2A]"
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>
              {!hasPassword && (
                <p className="text-[#6F6F6F] text-[11px] leading-relaxed">
                  After setting a password, you'll be able to login with your phone number and password from{' '}
                  <span className="text-[#D4A843]">/account/login</span>.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 px-1">
          <CheckCircle2 size={13} className="text-[#2FBF71]" />
          <p className="text-[#6F6F6F] text-xs">Account active · Last login {customer?.last_login_at ? formatDateTime(customer.last_login_at) : 'just now'}</p>
        </div>
      </div>
    </CustomerShell>
  );
}
